import React from 'react';
import isHotkey, { KeyboardEventLike } from 'is-hotkey';
import { v4 } from 'uuid';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { ShortcutKeyView, ShortcutCommandEnumType } from '@mark43/cad-api';
import useEventListener from '../common/hooks/use-event-listener';
import { useBroadcast } from '../common/broadcast-channel/use-broadcast';
import { useDrawerControls } from '../drawers/components/drawer-context-provider';
import { useWorkspaceStore } from '../panels/use-workspace-store';
import { useOpenCommandLinePanel } from '../command-line/use-open-command-line-panel';
import { useOpenDepartmentLink } from '../layout/use-open-department-link';
import { useOpenWorkspacePanel } from '../panels/use-open-workspace-panel';
import { useClearDexResultsMutation } from '../data-exchange/results/use-clear-dex-results-mutation';
import { useEntityPanelManager } from '../panels/entity-panels/monitored-entities/state/entity-panel-manager';
import { useHandleEventsWindowShortcutKeys } from './use-handle-events-window-shortcut-keys';
import { useIntegratedActionBroadcaster } from '../action-broadcaster/use-integrated-action-broadcaster';
import { toggleCommandLineHeight } from '../command-line/helpers';
import { useWorkspaceWindowType } from '../common/hooks/use-workspace-window-type';
import { getM43LogRocket } from '../monitoring/logrocket';
import { useUserActionTracker } from '../monitoring/user-action-tracker/use-user-action-tracker';
import { useApplicationType } from '../common/hooks/use-application-type';
import { useLogout } from '../common/hooks/use-logout';
import { useMappedShortcutKeyViews } from '../common/hooks/use-mapped-shortcut-key-views';
import { useOpenEventSearch } from '../common/hooks/use-open-event-search';
import { useOpenUnitStatusSearch } from '../common/hooks/use-open-unit-status-search';
import { electronBridge } from '../electron-bridge/electron-bridge';
import { useUnitEmergencyUtils } from '../unit-emergency/use-unit-emergency-utils';
import {
    useFullscreenModalControls,
    useFullscreenModalState,
} from '../modals/context/modal-context-provider';
import { useSavePreferences } from '../preferences/use-save-preferences';
import { useThemeStore } from '~/theme/use-theme-store';
import { useToast } from '@cad-arc';
import { errorOccurredWhile } from '~/common/helpers/error-occurred-while';

type ShortcutKeysInvoker = (shortcutKeyView: ShortcutKeyView) => void;
const ShortcutKeysContext = React.createContext<undefined | ShortcutKeysInvoker>(undefined);

export function useShortcutKeysInvoker(): ShortcutKeysInvoker {
    const invoker = React.useContext(ShortcutKeysContext);
    if (!invoker) {
        throw new Error(
            'Unexpectedly did not find `ShortcutKeysContext` value. Make sure that you have rendered the provider in your component tree.'
        );
    }
    return invoker;
}

export function ShortcutKeysListener(props: { children: React.ReactElement }): React.ReactElement {
    const { postMessage: legacyPostMessage } = useBroadcast('legacy-store-broadcast');
    const { postMessage: preferencesPostMessage } = useBroadcast('preferences-notifications');
    const drawerControls = useDrawerControls();
    const enabledFeatures = useEnabledFeatures();
    const { isFirstResponder, isElectronRewrite } = useApplicationType();
    const openEventSearch = useOpenEventSearch();
    const openUnitStatusSearch = useOpenUnitStatusSearch();
    const { handleEmergencyTimer, timer } = useUnitEmergencyUtils();
    const handleEventsWindowShortcutKeys = useHandleEventsWindowShortcutKeys();
    const workspacePanelsStore = useWorkspaceStore();
    const windowType = useWorkspaceWindowType();
    const eventPanelManager = useEntityPanelManager();
    const createCommandLinePanel = useOpenCommandLinePanel();
    const openDepartmentLink = useOpenDepartmentLink();
    const openWorkspacePanel = useOpenWorkspacePanel();
    const clearDexResultsMutation = useClearDexResultsMutation();
    const userActionTracker = useUserActionTracker();
    const { trigger } = useIntegratedActionBroadcaster();
    const handleLogout = useLogout();
    const mappedShortcutKeyViews = useMappedShortcutKeyViews();
    const shouldAllowUserToOpenMap =
        enabledFeatures.disableLegacyMapAndSearches || enabledFeatures.mapWindowEnabled;
    const modalControls = useFullscreenModalControls();
    const modalState = useFullscreenModalState();
    const { isDarkMode, inactiveTheme } = useThemeStore();
    const toast = useToast();

    // save user preferences when the theme change shortcut key is used
    const savePreferences = useSavePreferences({
        onSuccess: () => {
            preferencesPostMessage({
                type: 'THEME_CHANGED',
                payload: { theme: inactiveTheme },
            });
        },
        onError: () => {
            toast({
                description: errorOccurredWhile('trying to save your theme change'),
                status: 'error',
            });
        },
    });

    const shortcutKeyInvoker = React.useCallback(
        (shortcutKeyView: ShortcutKeyView) => {
            getM43LogRocket()?.debug('User attempting keyboard shortcut', shortcutKeyView);
            userActionTracker?.trackShortcut({
                shortcutKeyView,
            });
            const { activeWorkspaceWindowType } = workspacePanelsStore;

            if (isElectronRewrite) {
                if (shortcutKeyView.shortcutCommand === 'LOGOUT') {
                    trigger([{ id: v4(), targetWindowType: 'ROOT', type: 'LOGOUT' }]);

                    return;
                }
            }

            if (shortcutKeyView.shortcutCommand === 'NEW_EVENT') {
                trigger([
                    {
                        id: v4(),
                        targetWindowType: 'ROOT',
                        type: 'OPEN_EVENT_V2_WINDOW',
                    },
                    {
                        id: v4(),
                        targetWindowType: 'EVENT_V2',
                        type: 'OPEN_EVENT_PANEL',
                        payload: {},
                    },
                ]);
                userActionTracker?.trackNewEvent({
                    newEventTrigger: 'SHORTCUT',
                });
                return;
            }
            if (shortcutKeyView.shortcutCommand === 'EVENTS') {
                trigger([
                    {
                        id: v4(),
                        targetWindowType: 'ROOT',
                        type: 'OPEN_EVENT_V2_WINDOW',
                    },
                    {
                        id: v4(),
                        targetWindowType: 'EVENT_V2',
                        type: 'FOCUS_ACTIVE_EVENT_WINDOW_PANEL',
                    },
                ]);
                return;
            }
            if (shouldAllowUserToOpenMap && shortcutKeyView.shortcutCommand === 'AVL_MAP') {
                trigger([
                    {
                        id: v4(),
                        targetWindowType: 'ROOT',
                        type: 'OPEN_MAP_V2_WINDOW',
                    },
                ]);
                return;
            }
            if (
                shortcutKeyView.shortcutContext === 'DISPATCHER_EVENT_PANEL' ||
                shortcutKeyView.shortcutContext === 'DISPATCHER_EVENT_WINDOW'
            ) {
                handleEventsWindowShortcutKeys(shortcutKeyView);
                return;
            }
            if (shortcutKeyView.shortcutCommand === 'DEX_UPDATE_TO_EVENT') {
                if (
                    activeWorkspaceWindowType === 'EVENT_V2' ||
                    activeWorkspaceWindowType === 'QUEUE_V2'
                ) {
                    trigger([
                        {
                            id: v4(),
                            targetWindowType: activeWorkspaceWindowType,
                            type: 'DEX_RESULTS_PANEL_UPDATE_TO_EVENT',
                        },
                    ]);
                }
                return;
            }
            switch (shortcutKeyView.shortcutCommand) {
                case 'GO_TO_M43_RMS':
                    openDepartmentLink({
                        path: '',
                        app: 'RMS',
                        tenantStrategy: 'PREFER_RMS_TENANT',
                    });
                    break;
                case 'CLOSE_PANEL': {
                    const focusedPanel = workspacePanelsStore.getFocusedPanel();
                    if (focusedPanel) {
                        focusedPanel.close();
                    }
                    break;
                }
                case 'LOGON_UNIT':
                    drawerControls.open({
                        type: 'log-on-edit-unit',
                        props: {},
                    });
                    break;
                case 'COMMAND_LINE': {
                    const commandLinePanel = workspacePanelsStore.activePanels.find(
                        (p) => p.workspacePanelType === 'COMMAND_LINE'
                    );

                    if (commandLinePanel) {
                        commandLinePanel.focus();
                        if (
                            activeWorkspaceWindowType !== 'EVENT_V2' &&
                            activeWorkspaceWindowType !== 'QUEUE_V2'
                        ) {
                            throw new Error(
                                `Unexpectedly found unsupported active workspace window type for CLI focusing: ${activeWorkspaceWindowType}`
                            );
                        }
                        trigger([
                            {
                                id: v4(),
                                targetWindowType: activeWorkspaceWindowType,
                                type: 'FOCUS_COMMAND_LINE_PANEL',
                            },
                        ]);
                    } else {
                        createCommandLinePanel();
                    }
                    break;
                }
                case 'COMMAND_LINE_COMPACT_TOGGLE': {
                    const focusedPanel = workspacePanelsStore.getFocusedPanel();
                    if (focusedPanel && focusedPanel.workspacePanelType === 'COMMAND_LINE') {
                        toggleCommandLineHeight(focusedPanel);
                    } else if (
                        focusedPanel &&
                        (focusedPanel.workspacePanelType === 'EVENT' ||
                            focusedPanel.workspacePanelType === 'SCHEDULED_EVENT')
                    ) {
                        const eventPanel = eventPanelManager.getEventPanel(focusedPanel.uuid);
                        if (eventPanel) {
                            eventPanel.setEventPanelSize((curr) => {
                                if (curr === 'NORMAL') {
                                    return 'XL';
                                }
                                return 'NORMAL';
                            });
                        }
                    }
                    break;
                }
                case 'DATA_EXCHANGE_OPEN_PANELS':
                    openWorkspacePanel({
                        workspacePanelConfig: {
                            workspacePanelType: enabledFeatures.dexNextEnabled
                                ? 'DATA_EXCHANGE_FORM'
                                : 'DATA_EXCHANGE_FORMS',
                        },
                    });

                    openWorkspacePanel({
                        workspacePanelConfig: { workspacePanelType: 'DATA_EXCHANGE_RESULTS' },
                    });
                    break;
                case 'DATA_EXCHANGE_CLEAR_RESULTS':
                    clearDexResultsMutation.mutate();
                    break;
                case 'DATA_EXCHANGE_OPEN_FORM_VEHICLE_BY_PLATE':
                case 'DATA_EXCHANGE_OPEN_FORM_PERSON_BY_LICENSE':
                case 'DATA_EXCHANGE_OPEN_FORM_PERSON_BY_NAME':
                case 'DATA_EXCHANGE_OPEN_RESULTS':
                case 'EDIT_UNIT':
                case 'UNITS':
                case 'UPDATE_UNIT_LOCATION':
                case 'CURRENT_ASSIGNMENT':
                case 'AT_SCENE':
                case 'ASSIGNED_EVENTS':
                case 'UNASSIGNED_EVENTS':
                case 'EVENT_SAVE':
                case 'ALL_EVENTS':
                case 'TOGGLE_MESSAGING_PANEL': {
                    trigger([
                        {
                            id: v4(),
                            targetWindowType: 'ROOT',
                            type: shortcutKeyView.shortcutCommand,
                        },
                    ]);
                    break;
                }
                case 'CHANGE_UNIT_STATE':
                case 'SELF_INITIATE': {
                    trigger([
                        {
                            id: v4(),
                            targetWindowType: 'ROOT',
                            type: shortcutKeyView.shortcutCommand,
                            linkedEntityType: shortcutKeyView.linkedEntityType,
                            linkedEntityId: shortcutKeyView.linkedEntityId,
                        },
                    ]);
                    break;
                }
                case 'FIRST_RESPONDER_MAP':
                    if (shouldAllowUserToOpenMap) {
                        trigger([
                            {
                                id: v4(),
                                targetWindowType: 'ROOT',
                                type: 'OPEN_FR_MAP_V2_WINDOW',
                            },
                        ]);
                    }
                    break;
                case 'SHOW_CURRENT_LOCATION':
                    if (modalState === undefined || modalState.type === 'SHORTCUT_KEYS') {
                        modalControls.open({
                            type: 'SHOW_UNIT_CURRENT_LOCATION',
                            props: {},
                        });
                    } else {
                        modalControls.close();
                    }
                    break;
                case 'LOGOUT':
                    handleLogout();
                    break;
                case 'CHECK_FOR_UPDATES':
                    if (electronBridge && 'isElectronRewrite' in electronBridge) {
                        electronBridge.checkForUpdates();
                    }
                    break;
                case 'UNIT_EMERGENCY':
                    if (!timer) {
                        handleEmergencyTimer();
                    }
                    break;
                case 'UNIT_EMERGENCY_CANCEL':
                    if (timer) {
                        handleEmergencyTimer();
                    }
                    break;
                case 'CAD_SEARCH':
                    openEventSearch();
                    break;

                case 'UNIT_STATUS_SEARCH':
                    openUnitStatusSearch();
                    break;

                case 'SAVED_SEARCHES':
                    openEventSearch();
                    trigger([
                        {
                            id: v4(),
                            targetWindowType: 'EVENT_SEARCH_V2',
                            type: 'OPEN_SAVED_SEARCHES',
                        },
                    ]);

                    break;

                case 'UNIT_STATUS_SAVED_SEARCHES':
                    openUnitStatusSearch();
                    trigger([
                        {
                            id: v4(),
                            targetWindowType: 'UNIT_STATUS_SEARCH_V2',
                            type: 'OPEN_SAVED_SEARCHES',
                        },
                    ]);
                    break;
                case 'TOGGLE_LIGHT_DARK_MODE':
                    if (enabledFeatures.allowLightModeEnabled) {
                        savePreferences({
                            preference: isFirstResponder
                                ? 'FIRST_RESPONDER_LIGHT_MODE_ENABLED'
                                : 'CAD_LIGHT_MODE_ENABLED',
                            value: isDarkMode ? true : false,
                        });
                    }
                    break;
                default:
                    // For all other cases (shortcut keys that can't be handled in the queue window) we send the event
                    // to the store window
                    legacyPostMessage({
                        type: 'SEND_SHORTCUT_KEY',
                        // we send the _original_ shortcutKeyCombination because that's expected by
                        // the epic that ends up firing in the store window
                        payload: shortcutKeyView.shortcutKeyCombination,
                    });
            }
        },
        [
            shouldAllowUserToOpenMap,
            trigger,
            handleEventsWindowShortcutKeys,
            openDepartmentLink,
            drawerControls,
            openWorkspacePanel,
            clearDexResultsMutation,
            legacyPostMessage,
            workspacePanelsStore,
            createCommandLinePanel,
            eventPanelManager,
            handleLogout,
            openEventSearch,
            openUnitStatusSearch,
            isElectronRewrite,
            isFirstResponder,
            handleEmergencyTimer,
            timer,
            modalControls,
            modalState,
            userActionTracker,
            enabledFeatures.dexNextEnabled,
            savePreferences,
            enabledFeatures.allowLightModeEnabled,
            isDarkMode,
        ]
    );

    const canExecuteShortcutOnFocusedEventPanel = React.useCallback(
        (isNewEvent: boolean, command: ShortcutCommandEnumType) => {
            if (command === 'EVENT_CAUTIONS_TAB') {
                return !isNewEvent;
            }

            return true;
        },
        []
    );

    const findShortcutKeyViewByContext = React.useCallback(
        (e: KeyboardEventLike) => {
            const views = mappedShortcutKeyViews.filter((shortcutKeyView) => {
                return isHotkey(shortcutKeyView.mappedShortcutKeyCombination, e);
            });
            const shortcutContext = isFirstResponder ? 'FIRST_RESPONDER' : 'DISPATCHER';
            const defaultShortcut = views.find((v) => v.shortcutContext === shortcutContext);
            // If we're in the event window
            if (!isFirstResponder && windowType === 'EVENT_V2') {
                const { workspacePanelType, linkedEntityId } =
                    workspacePanelsStore.getFocusedPanel() ?? {};

                // If we're focusing an event panel, try to find matching shortcut in the event panel context.
                if (workspacePanelType === 'EVENT' || workspacePanelType === 'SCHEDULED_EVENT') {
                    const eventPanelShortcut = views.find(
                        (v) => v.shortcutContext === 'DISPATCHER_EVENT_PANEL'
                    );

                    if (
                        eventPanelShortcut &&
                        canExecuteShortcutOnFocusedEventPanel(
                            !linkedEntityId,
                            eventPanelShortcut.shortcutCommand
                        )
                    )
                        return eventPanelShortcut;
                }

                // Otherwise try to find shortcut for the event window
                return (
                    views.find((v) => v.shortcutContext === 'DISPATCHER_EVENT_WINDOW') ??
                    defaultShortcut
                );
            }

            return defaultShortcut;
        },
        [
            canExecuteShortcutOnFocusedEventPanel,
            mappedShortcutKeyViews,
            windowType,
            workspacePanelsStore,
            isFirstResponder,
        ]
    );

    const shortcutKeyHandler = React.useCallback(
        (e: KeyboardEvent) => {
            if (!enabledFeatures.shortcutKeysEnabled || e.repeat) return;
            const shortcutKeyView = findShortcutKeyViewByContext(e);
            if (shortcutKeyView) {
                e.preventDefault();
                e.stopPropagation();
                shortcutKeyInvoker(shortcutKeyView);
            }
        },
        [enabledFeatures.shortcutKeysEnabled, findShortcutKeyViewByContext, shortcutKeyInvoker]
    );

    useEventListener('keydown', shortcutKeyHandler, undefined, {
        passive: false,
    });

    return (
        <ShortcutKeysContext.Provider value={shortcutKeyInvoker}>
            {props.children}
        </ShortcutKeysContext.Provider>
    );
}
